<template>
    <div class="surface-primary mt-auto">
        <div class="px-6 xl:px-0">
            <div
                v-if="entry.footer_TopText && entry.footer_TopText.length > 0"
                class="container-extra-wide mx-auto pt-10 lg:pt-20"
            >
                <div class="grid gap-9 lg:auto-cols-fr lg:grid-flow-col lg:gap-16">
                    <div
                        v-for="(topText, topTextIndex) in entry.footer_TopText"
                        :key="topTextIndex"
                    >
                        <p class="font-heading text-heading font-brand-heading mb-4 text-xl">
                            {{ topText.footerTopTextItem_Heading }}
                        </p>
                        <p class="leading-sm font-ux text-sm text-(--text-body)">
                            {{ topText.footerTopTextItem_BodyText }}
                        </p>
                    </div>
                </div>
                <div
                    class="mt-10 mb-10 h-px w-full bg-(--border-neutral-subtle) lg:mt-20 lg:mb-5"
                    aria-hidden="true"
                />
            </div>
            <div class="container-extra-wide mx-auto flex flex-col gap-9 pt-10 lg:flex-row lg:pt-20">
                <div
                    v-if="hasFooterLogo"
                    class="flex-1"
                >
                    <slot name="logo" />
                </div>

                <div
                    class="flex-1"
                    :class="{ 'flex-initial': hasMultipleContacts && hasFooterLogo }"
                >
                    <div :class="{ 'grid gap-9 lg:grid-cols-2': hasMultipleContacts }">
                        <div
                            v-for="(contactDetails, contactDetailIndex) in entry.footer_ContactDetails"
                            :key="contactDetailIndex"
                        >
                            <div
                                class="flex flex-col gap-y-3"
                                :class="{
                                    'items-center justify-center': alignment === 'centred',
                                }"
                            >
                                <p
                                    v-if="contactDetails.footerContactDetailsItem_Heading"
                                    class="text-subheading mb-3 text-xs font-medium uppercase"
                                >
                                    {{ contactDetails.footerContactDetailsItem_Heading }}
                                </p>
                                <NuxtLink
                                    v-for="(contact, contactIndex) in contactDetails.footerContactDetailsItem_Links"
                                    :key="contactIndex"
                                    :to="contact.url"
                                    :target="contact.target"
                                    class="flex items-center space-x-1.5"
                                >
                                    <DIcon
                                        v-if="contact?.icon && contact.icon.name"
                                        :icon="iconName(contact.icon)"
                                        class="shape-brand-accent"
                                    />
                                    <span
                                        class="text-neutral-extra-strong text-sm font-medium underline-offset-2 hover:underline"
                                    >
                                        {{ contact.linkText }}
                                    </span>
                                </NuxtLink>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="flex-1"
                    :class="{ 'flex-initial': hasMultipleContacts && hasFooterLogo }"
                >
                    <template v-if="entry.footer_Quicklinks?.length">
                        <p
                            v-if="entry.footer_QuicklinksHeading"
                            class="text-subheading mb-3 text-xs font-medium uppercase"
                        >
                            {{ entry.footer_QuicklinksHeading }}
                        </p>
                        <div
                            v-if="entry.footer_Quicklinks?.length"
                            class="columns-2"
                            :class="{
                                'items-center justify-center': alignment === 'centred',
                            }"
                        >
                            <NuxtLink
                                v-for="(quicklink, quicklinkIndex) in entry.footer_Quicklinks"
                                :key="quicklinkIndex"
                                :to="quicklink.url"
                                :target="quicklink.target"
                                class="text-neutral-extra-strong block py-1 text-sm font-medium underline-offset-2 hover:underline"
                                no-prefetch
                            >
                                {{ quicklink.text }}
                            </NuxtLink>
                        </div>
                    </template>
                </div>
            </div>

            <div class="container-extra-wide mx-auto">
                <div class="mt-12 mb-6 h-px w-full bg-(--border-neutral-subtle)" />
                <p
                    v-if="entry.footer_DisclaimerText?.length > 0"
                    class="text-neutral-subtle leading-sm font-ux container mb-5 text-sm"
                    :class="{
                        'mx-auto text-center': alignment === 'centred',
                    }"
                >
                    {{ entry.footer_DisclaimerText }}
                </p>

                <div
                    class="container-extra-wide flex flex-col-reverse flex-wrap items-center justify-between gap-y-6 pb-6 leading-none lg:flex-row"
                >
                    <div
                        class="flex flex-col items-center gap-y-6 lg:flex-row lg:divide-x lg:divide-(--border-neutral-base)"
                    >
                        <div class="pr-3">
                            <slot name="copyright" />
                        </div>

                        <NuxtLink
                            v-for="(policy, policyIndex) in entry.footer_PolicyLinks"
                            :key="policyIndex"
                            :to="policy.url"
                            :target="policy.target"
                            class="text-neutral-extra-strong px-3 text-sm font-medium"
                            :class="{
                                'underline underline-offset-2 hover:underline lg:no-underline': !!policy.url,
                            }"
                        >
                            {{ policy.linkText }}
                        </NuxtLink>

                        <div class="pl-3">
                            <slot name="siteByDigistorm" />
                        </div>
                    </div>

                    <!-- social links -->
                    <div
                        v-if="socialLinks"
                        class="flex shrink-0 flex-wrap gap-x-5 gap-y-2 pl-3"
                        :class="{
                            'items-center justify-center': alignment === 'centred',
                        }"
                    >
                        <NuxtLink
                            v-for="(social, socialIndex) in socialLinks"
                            :key="socialIndex"
                            :to="social.url"
                            :target="social.target"
                            :aria-label="social.title || social.text || social.ariaLabel"
                        >
                            <DButton
                                v-if="social?.icon && social.icon.name"
                                :icon="iconName(social?.icon)"
                                class="social-media-button"
                                theme="transparent"
                                size="sm"
                            />
                        </NuxtLink>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="entry?.footer_AccreditationLogos?.length"
            :data-mode="footerAccreditationMode"
            class="surface-primary px-6 xl:px-0"
        >
            <div
                class="container-extra-wide mx-auto"
                :class="{
                    'pt-6 pb-10 lg:pt-10 lg:pb-20': !footerAccreditationMode || footerAccreditationMode === mode,
                    'py-6': footerAccreditationMode && footerAccreditationMode !== mode,
                }"
            >
                <AccreditationLogos
                    :logos="entry.footer_AccreditationLogos"
                    :centred="alignment === 'centred'"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
    import { DIcon, DButton } from '@digistorm/spark'

    import AccreditationLogos from '../AccreditationLogos.vue'

    const { colorPalette } = useColorPalette()
    const { iconName } = useIcons()

    const props = defineProps({
        entry: Object,
        socialLinks: Object,
        hasFooterLogo: Boolean,
    })

    const alignment = computed(() => {
        return get(props.entry, 'footer_Alignment')
    })

    const hasMultipleContacts = computed(() => {
        return get(props.entry, 'footer_ContactDetails', [])?.length > 1
    })

    const mode = computed(() => {
        return colorPalette(get(props.entry, 'footer_ColorPalette'))
    })

    const footerAccreditationMode = computed(() => {
        return colorPalette(get(props.entry, 'footer_AccreditationLogosPalette'))
    })
</script>
